import { type ReactNode } from "react";
import { type PdpPageProductData } from "../models/PdpPageProductData";

import { CompanyLogo } from "../../common/components/companyLogo/CompanyLogo";
import { ExtendedPdpSummary } from "./ProductSummary/ExtendedPdpSummary";
import { DefaultPdpSummary } from "./ProductSummary/DefaultPdpSummary";
import { userIsCustomer, userIsSalesman } from "../../utils/roles";
import { SalesmanPdpSummary } from "./ProductSummary/SalesmanPdpSummary";
import { IncludedPartsSection } from "./Housing/IncludedPartsSection";
import { OptionalPartsSection } from "./Housing/OptionalPartsSection";
import { isKITProduct } from "../../models/ProductData";
import { QuantityChangedSnackbar } from "./Housing/HousingQtyChangedSnackbar";
import { useTranslation } from "react-i18next";
import { CampaignRecommendations } from "~/feature/SaleRecommendations/CampaignRecommendations/CampaignRecommendations";
import type { CommonProductDetailsPageProps } from "~/ProductDetails/ProductDetailsPage";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { usePdpTabs } from "./usePDPTabs";
import { Stack } from "@mui/material";
import { Tab } from "~/common/m3/Tabs/Tab";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import { Permission, userHasPermission } from "~/utils/permissions";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { useScript } from "~/common/hooks/useScript";

export interface ProductDetailsPageProps extends CommonProductDetailsPageProps {
  postSummarySlot?: ReactNode;
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  preferredProductsBlock?: ReactNode;
}

export const DesktopProductDetailsPage = (props: ProductDetailsPageProps) => {
  const productDescriptor = props.productDescriptor;
  const product = productDescriptor.base;

  const { t } = useTranslation();

  const { tabs, onTabActivationHandler, activeTabIndex } = usePdpTabs(productDescriptor);

  const { preferredProducts } = props;
  const preferredProductCodes = preferredProducts.data?.preferred.map((product) => product.code) ?? [];
  const hasPreferredProducts = preferredProductCodes.length > 0;

  let summary;
  // Order is important!
  // Special case for Ewellix
  if (product.externalProduct) {
    summary = (
      <DefaultPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        changeTab={onTabActivationHandler}
      />
    );
  }
  // Salesman is a customer too, so has to be first
  else if (userIsSalesman()) {
    summary = (
      <SalesmanPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        changeTab={onTabActivationHandler}
      />
    );
  } else {
    const canSeeExtendedPdpSummary = userIsCustomer() && userHasPermission(Permission.AVAILABILITY);

    summary = canSeeExtendedPdpSummary ? (
      <ExtendedPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        preferredProductCodes={preferredProductCodes}
        changeTab={onTabActivationHandler}
      />
    ) : (
      <DefaultPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        changeTab={onTabActivationHandler}
      />
    );
  }

  let similarOnSalesBlock = null;
  const products = preferredProducts.data?.sales ?? [];
  if (window.SCHA.feature.showSimilarSalesBlockOnPdp && products?.length) {
    similarOnSalesBlock = (
      <CampaignRecommendations
        products={preferredProducts.data?.sales ?? undefined}
        variant={"PDP"}
        className={"scha__pdp__similar-sales"}
      />
    );
  }

  useScript("https://orca-cdn.zoovu.com/core-js/zoovu-tag-library/zoovu-tag-library-latest.min.js");

  return (
    <div className={"scha__pdp pdp-desktop"}>
      <div className={"scha__print-only scha__pdp__print-logo"}>
        <CompanyLogo />
      </div>
      <div className={"scha__print-only scha__pdp__timestamp"}>{new Date().toTimeString()}</div>
      <Stack
        sx={{
          gap: { phone: 3, "desktop-s": 4 },
        }}
      >
        {similarOnSalesBlock}
        <Stack gap={8}>
          <ProductDescriptorContext value={productDescriptor}>{summary}</ProductDescriptorContext>
          {props.postSummarySlot}
        </Stack>
        {isKITProduct(product) && (
          <Stack gap={4}>
            <QuantityChangedSnackbar />
            {(product.includedParts?.length ?? 0) > 0 && (
              <IncludedPartsSection housingProducts={product.includedParts} />
            )}
            {(product.optionalParts?.length ?? 0) > 0 && (
              <OptionalPartsSection housingProducts={product.optionalParts} />
            )}
          </Stack>
        )}
        {/* to scroll to tabs from start add to url #tabs at the end */}

        {props.preferredProductsBlock}

        <Tabs
          id="tabs"
          onChange={(e, val) => {
            onTabActivationHandler(val);
          }}
          value={activeTabIndex}
          fullWidthIndicator={false}
          variant={"fullWidth"}
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={t(tab.title)} data-test-id={tab.testId} />
          ))}
        </Tabs>
        {tabs.map((tab) => (
          <div key={tab.value} hidden={activeTabIndex !== tab.value}>
            {tab.component}
          </div>
        ))}
      </Stack>
    </div>
  );
};

export const SCROLL_TO_TABS = () => {
  window.setTimeout(() => document.getElementById("tabs")?.scrollIntoView(), 16);
};
